<template>
  <div class="message-view">
    <van-nav-bar
      class="file-header nav-bar-bgcolor"
      @click-left="back"
    >
      <i slot="left" class="iconfont icon-back"/>
      <span slot="title" @click="toggleMessageFilter">
        {{messageFilterSelectedName}}
        <i :class="['iconfont', `icon-arrow-${showMessageFilter ? 'up' : 'down'}`]"/>
      </span>
    </van-nav-bar>
    <ul :class="['message-main', !messages.length && 'content-center']" @scroll.passive="onScroll">
      <van-loading v-if="isFetching && !messages.length"/>
      <template v-else-if="!messages.length">
        <svg-icon icon="empty-default"/>
        <p v-if="messageFilterSelected === 'all'">{{$t('message.empty')}}</p>
        <p v-else>{{$t('message.emptyFilterMessage')}}</p>
      </template>
      <li v-for="item of messages" :key="item.id" @click="handleViewMessage(item)">
        <message-item :item="item" @message-delete="handleRemoveMessage"/>
      </li>
      <li v-if="pageNumber < pageCount && messages.length" key="more" class="loading-more">
        <van-loading/>
        {{$t('base.loading')}}
      </li>
    </ul>
    <div v-show="showMessageFilter" class="van-modal" @click="toggleMessageFilter"></div>
    <div :class="['message-filter', showMessageFilter && 'show']">
      <ul>
        <li
          v-for="(action, idx) of actions"
          :class="['action-item', action.className]"
          :key="action.key"
          @click="action.callback"
        >
          <div :class="['content', idx && 'has-border-top']">
            {{action.name}}
          </div>
        </li>
      </ul>
    </div>
    <van-popup
      class="child-message-list"
      v-model="showChildMessageList"
      position="right"
      :overlay="false"
    >
      <child-message-list :show="showChildMessageList"/>
    </van-popup>
    <van-popup
      class="share-link-view"
      v-model="showShareLink"
      position="right"
    >
      <share-link
        v-if="$route.params.type === 'share_link'"
        :uniqueName="$route.query.unique_name"
      />
    </van-popup>
    <!-- comment -->
    <van-popup
      class="comment-view"
      v-model="showComment"
      position="right"
    >
      <comment-view
        :show="showComment"
        :showItem="true"
        @close-comment="closeCommentView"
        @item-preview="togglePreview"
      />
    </van-popup>
    <!-- comment end -->
  </div>
</template>
<script>
import config from '@/config'
import debounce from 'lodash/debounce'
import ajax from '@/utils/ajax'
import {i18n} from '@/utils/i18n'
import {endsWith} from '@/utils/common'
import {MESSAGES_INFO} from '@/config/api'
import ListFilter from '../utils/ListFilter'
import MessageItem from './MessageItem'
import ChildMessageList from './ChildMessageList'
import ShareLink from '../shareLink/ShareLink'
import CommentView from '../comment/CommentView'

const filterList = [
  {
    id: 'all',
    name: i18n.t('message.type.all')
  },
  {
    id: 'comment',
    name: i18n.t('message.type.comment')
  },
  {
    id: 'share_link',
    name: i18n.t('message.type.share')
  },
  {
    id: 'collab',
    name: i18n.t('message.type.collab')
  }
]

export default {
  name: 'MessageView',
  i18n,
  components: {
    ListFilter,
    MessageItem,
    ChildMessageList,
    ShareLink,
    CommentView
  },
  data () {
    return {
      isFetching: false,
      messages: [],
      filterList,
      filterName: i18n.t('message.filter'),
      messageFilterSelected: filterList[0].id,
      messageFilterSelectedName: filterList[0].name,
      pageNumber: 1,
      requestPageNumber: 1,
      pageCount: 1,
      showMessageFilter: false,
      showChildMessageList: this.$route.params.type === 'collab',
      showShareLink: this.$route.params.type === 'share_link',
      showComment: this.$route.params.type === 'comment'
    }
  },
  computed: {
    actions () {
      return this.filterList.map(item => {
        const { id, name } = item
        return {
          key: id,
          name,
          className: id === this.messageFilterSelected && 'primary-color',
          callback: () => this.handleFilterSeleted(id)
        }
      })
    }
  },
  watch: {
    $route (to) {
      this.showChildMessageList = to.params.type === 'collab'
      this.showShareLink = to.params.type === 'share_link'
      this.showComment = to.params.type === 'comment'
      this.showMessageFilter = to.query.message_filter
    },
    messageFilterSelected (type) {
      this.messages = []
      this.pageNumber = 1
      this.requestPageNumber = 1
      this.fetchList()
    },
    'store.actionState' (state) {
      const { action, type, id, name } = state
      let itemInList

      switch (action) {
        case 'rename':
          itemInList = this.messages.find(item => {
            if (item.object_typed_id === `${type}_${id}`) {
              return true
            }
          })

          if (itemInList && itemInList.most_recent_message) {
            Object.assign(itemInList.most_recent_message, {item_name: name})
          }
          break
        case 'markRead':
          let unread_count = 0
          itemInList = this.messages.find(item => {
            // eslint-disable-next-line eqeqeq
            if (item.id == id) {
              unread_count = item.unread_count
              return true
            }
          })
          if (itemInList) {
            config.unread_messages_count = Math.max(config.unread_messages_count - unread_count, 0)
            Object.assign(itemInList, {unread_count: Math.max(itemInList.unread_count - unread_count, 0)})
          }
          break
      }
    }
  },
  beforeCreate () {
    const query = this.$route.query
    if (!Object.keys(query).length) return
    const reservedQuery = ['comment', 'preview', 'file_id', 'unique_name', 'scenario', 'typed_id']
    const newQuery = {}
    reservedQuery.forEach(item => {
      newQuery[item] = query[item]
    })
    this.$router.replace({ query: newQuery })
  },
  created () {
    this.fetchList()
  },
  methods: {
    back () {
      this.$router.go(-1)
    },
    toggleMessageFilter () {
      this.toggleQuery('message_filter')
    },
    fetchList () {
      let url = `${MESSAGES_INFO}?page_number=${this.requestPageNumber}`
      if (this.messageFilterSelected && this.messageFilterSelected !== 'all') {
        url += `&types=${this.messageFilterSelected}`
      } else {
        url += '&types=comment,share_link,collab'
      }
      this.requestQuery = {}
      this.requestQuery.type = this.messageFilterSelected
      this.isFetching = true
      ajax({ url, requestQuery: this.requestQuery }).then(res => {
        if (JSON.stringify(this.requestQuery) !== JSON.stringify(res.requestQuery)) {
          return
        }
        if (res && res.success) {
          res.messages.forEach(item => { this.convertMessageType(item) })
          if (res.page_number === 1) {
            this.messages = res.messages
          } else {
            this.messages = this.messages.concat(res.messages)
          }
          this.pageCount = res.page_count
          this.pageNumber = res.page_number
          if (!this.requestQuery.type || this.requestQuery.type === 'all') {
            config.unread_messages_count = res.total_unread_message_count
          }
        }
        this.isFetching = false
      })
    },
    onScroll: debounce(function (e) {
      if (!this.isFetching && e.target.scrollTop + e.target.clientHeight > e.target.scrollHeight - 200 && this.pageNumber < this.pageCount) {
        this.requestPageNumber++
        this.fetchList()
      }
    }, 500),
    convertMessageType (item) {
      let type = item.type
      if (endsWith(type, '_notice')) {
        item.type = type.substring(0, type.length - 7)
        item.showNoticeOnly = true
      }
    },
    handleFilterSeleted (id) {
      this.messageFilterSelected = id
      this.filterList.forEach(item => {
        if (item.id === id) {
          this.messageFilterSelectedName = item.name
        }
      })

      this.toggleMessageFilter()
    },
    handleRemoveMessage (id) {
      this.messages = this.messages.filter(item => item.id !== id)
    },
    handleViewMessage (item) {
      const { type, id, object_typed_id, most_recent_message } = item
      const { query, params: {module} } = this.$route
      if (type === 'share_link' && most_recent_message && most_recent_message.unique_name) {
        query.unique_name = most_recent_message.unique_name
      } else if (type === 'comment') {
        query.typed_id = object_typed_id
      }
      this.$router.push({ params: { module, type, id }, query })
    },
    closeCommentView () {
      this.$router.go(-1)
    },
    togglePreview (item) {
      this.$emit('item-preview', item)
    }
  }
}
</script>
<style lang="postcss">
@import 'variables';

.share-link-view {
  width: 100%;
  height: 100%;
}

.message-view {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: #eeeff5;

  &>* {
    flex: 0 0 auto;
  }

  & .message-main {
    flex: 1 1 auto;
    overflow: auto;
  }

  & li {
    &:last-of-type {
      & .item-content {
        border-bottom: 0
      }
    }

    &.loading-more {
      display: flex;
      justify-content: center;
      align-items: center;
      color: #999;
      height: 50px;
      line-height: 40px;

      & .van-loading {
        width: 20px;
        height: 20px;
        margin-right: 10px;
      }
    }
  }

  & .message-filter {
    position: absolute;
    top: 44px;
    width: 100%;
    line-height: 50px;
    background-color: #fff;
    font-size: 18px;
    text-align: center;
    transform: translate3d(0, -108%, 0);
    transition: .3s ease-in;

    &.show {
      transform: translate3d(0, 0, 0);
      transition: .4s ease-out;
    }
  }

  & .child-message-list {
    width: 100%;
    height: 100%;
  }
}
</style>
